import { ComponentLoader } from '@/components/ComponentLoader';
import { SignDocumentStep } from '@/components/Documents/SignDocumentStep';
import { FlowCompletion } from '@/components/FlowCompletion';
import { NewGeneralDialog } from '@/components/NewGeneralDialog';
import { StepperStep } from '@/components/NewGeneralDialog/DialogStepper';
import {
  ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS,
  DOCUMENT_STATUS,
} from '@/constants/documents';
import { useGetDocumentById } from '@/hooks/Documents/useGetDocumentById';
import { useUpdateCompanyDocument } from '@/hooks/Documents/useUpdateCompanyDocument';
import { useGeneralDialogStepper } from '@/hooks/useGeneralDialogStepper';
import { useRequestDocumentSignature } from '@/hooks/useRequestDocumentSignature';
import { Documents } from '@/types/company';
import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useToastNotification } from '@treyd-io/treyd-ui/hooks/useToastNotification';
import { filter, isNaN, map, toNumber, toString, toUpper } from 'lodash';
import { useSession } from 'next-auth/react';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetsStep } from './AssetsStep';
import { DocumentOverview } from './DocumentOverview';
import { IncomeInformation } from './IncomeInformation';
import { LiabilitiesStep } from './LiabilitiesStep';
import { ReviewStep } from './ReviewStep';

interface Props {
  isModalOpen: boolean;
  onModalClose: () => void;
  document: Documents.DocumentType;
}

const dialogStepsLabels = filter(
  ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS,
  (step) => isNaN(Number(step))
);

const COMPLETED_STEP_INDEX =
  ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Sign + 1;

export const AssetsAndLiabilities = memo(function AssetsAndLiabilities(
  props: Props
) {
  const STEPS: StepperStep[] = useMemo(
    () =>
      map(dialogStepsLabels, (label) => ({
        label: toString(label),
        status: 'not_completed',
      })),
    []
  );

  const handleDialogClose = () => {
    props.onModalClose();
  };

  return (
    <NewGeneralDialog
      isOpen={props.isModalOpen}
      onClose={handleDialogClose}
      steps={STEPS}
      slotsProps={{
        dialogContent: {
          dividers: true,
        },
      }}>
      <ModalBody document={props.document} onModalClose={handleDialogClose} />
    </NewGeneralDialog>
  );
});

const ModalBody = ({
  onModalClose,
  document,
}: {
  document: Documents.DocumentType;
  onModalClose: () => void;
}) => {
  const {
    hideStepper,
    moveBackward,
    moveForward,
    moveToStep,
    activeStepIndex,
  } = useGeneralDialogStepper();
  const showToastMessage = useToastNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: session, status } = useSession({ required: true });
  const user = session?.user;
  const companyId = user?.company_id;
  const country = user?.company?.country;
  const invoicingCurrency = toString(user?.company?.invoicing_currency);
  const isLoading = status === 'loading';
  const documentId = document?.id;

  const {
    data: documentData,
    refetch,
    isPending: isLoadingCurrentDocument,
  } = useGetDocumentById({
    documentId: toNumber(documentId),
    companyId: toNumber(companyId),
  });

  const currentDocument = documentData?.data;

  const [formData, setFormData] = useState<
    Partial<Documents.DocumentType['data']>
  >({ ...document?.data });
  const { mutateAsync: updateCompanyDocument, isPending: isUpdatingDocument } =
    useUpdateCompanyDocument();
  const { mutateAsync: requestSignature, isPending: isRequestingSignature } =
    useRequestDocumentSignature();

  const isDocumentLoading =
    isUpdatingDocument || isRequestingSignature || isLoadingCurrentDocument;

  const handleUpdateCompanyDocument = async (
    values: Partial<Documents.DocumentType['data']>
  ) => {
    setFormData((prev) => ({ ...prev, ...values }));
    moveForward();
  };
  const handleSubmitDocument = async () => {
    // First update document with all collected form data
    await updateCompanyDocument(
      {
        companyId: toNumber(companyId),
        documentId: toNumber(documentId),
        data: formData,
      },
      {
        onSuccess: async () => {
          await requestSignature(
            {
              companyId: toNumber(companyId),
              documentId: toNumber(documentId),
            },
            {
              onSuccess: () => {
                refetch();
              },
              onError: () => {
                if (
                  currentDocument?.status === DOCUMENT_STATUS.PENDING_SIGNATURE
                )
                  return;
                showToastMessage({
                  type: 'error',
                  message: t('error_updating_document'),
                });
              },
            }
          );
        },
        onError: () => {
          if (currentDocument?.status === DOCUMENT_STATUS.PENDING_SIGNATURE)
            return;
          showToastMessage({
            type: 'error',
            message: t('error_updating_document'),
          });
        },
      }
    );
  };

  const onDone = () => {
    queryClient.invalidateQueries({
      queryKey: ['company-documents', companyId],
    });
    onModalClose();
  };

  const handleEditClick = (
    stepName: ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS
  ) => moveToStep(stepName);

  const handleSigningCompleted = () => {
    hideStepper();
    moveForward();
  };

  const STEPS_CONTENT = [
    <DocumentOverview
      key={
        ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS[
          'Statement of assets & liabilities'
        ]
      }
      onMovingForward={handleUpdateCompanyDocument}
      formData={formData}
    />,
    <AssetsStep
      key={ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Assets}
      formData={formData}
      currency={toUpper(invoicingCurrency)}
      onMovingForward={handleUpdateCompanyDocument}
      onMovingBackward={moveBackward}
    />,
    <LiabilitiesStep
      key={ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Liabilities}
      formData={formData}
      currency={toUpper(invoicingCurrency)}
      onMovingForward={handleUpdateCompanyDocument}
      onMovingBackward={moveBackward}
    />,
    <IncomeInformation
      formData={formData}
      currency={toUpper(invoicingCurrency)}
      onMovingForward={handleUpdateCompanyDocument}
      onMovingBackward={moveBackward}
    />,

    <ReviewStep
      key={ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Review}
      formData={formData}
      documentId={documentId}
      onSubmit={handleSubmitDocument}
      onMovingForward={moveForward}
      onMovingBackward={moveBackward}
      isLoading={isDocumentLoading}
      onEditClick={handleEditClick}
    />,
    <Box maxWidth={960} margin="auto" marginBottom={6}>
      {country && currentDocument && (
        <SignDocumentStep
          handleNextStep={handleSigningCompleted}
          handleOnBackClick={moveBackward}
          country={country}
          documentId={documentId}
          key={ASSETS_AND_LIABILITIES_DIALOG_STEPS_LABELS.Sign}
        />
      )}
    </Box>,
    <FlowCompletion
      key={COMPLETED_STEP_INDEX}
      title={t('document_signed')}
      subtitle={t(
        'thank_you_for_providing_the_required_information_we_will_review_your_submission_and_reach_out_if_anything_further_is_needed'
      )}
      actionButtons={
        <Button size="large" variant="contained" onClick={onDone}>
          {t('done')}
        </Button>
      }
    />,
  ];

  if (isLoading) return <ComponentLoader />;

  return STEPS_CONTENT[activeStepIndex];
};
